import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Trophy, Calendar, Clock, AlertCircle, ArrowLeft, Globe, Users, Award, Home, GraduationCap, ShoppingBag, Code, ChevronLeft, ChevronRight } from 'lucide-react';
import { Event, Competition, CompetitionsByYear } from '../types/index';
import { apiService } from '../services/api';

const CompetitionsPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null);
  const [competitions, setCompetitions] = useState<CompetitionsByYear>({});
  const [loading, setLoading] = useState(true);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const response = await apiService.getEvents();
      if (response.success && response.data) {
        const events: Event[] = response.data;
        const competitionsData = events.filter((event: Event) => event.type === 'COMPETITION');
        

        // Organiser par année
        const competitionsByYear: CompetitionsByYear = {};
        const years = new Set<number>();

        competitionsData.forEach((competition: Event) => {
          const date = new Date(competition.dateStart);
          const year = date.getFullYear();
          years.add(year);

          if (!competitionsByYear[year]) {
            competitionsByYear[year] = [];
          }

          // Statut de la compétition : "À venir", "En cours", "Passée"
          const now = new Date();
          const startDate = new Date(competition.dateStart);
          const endDate = competition.dateEnd ? new Date(competition.dateEnd) : new Date(startDate.getTime() + 3 * 24 * 60 * 60 * 1000);
          let competitionStatus = 'Passée';
          if (now < startDate) {
            competitionStatus = 'À venir';
          } else if (now >= startDate && now <= endDate) {
            competitionStatus = 'En cours';
          }

          // Statut des inscriptions : priorité au champ manuel, sinon calcul automatique
          let registrationStatus = 'Fermées';
          if (competition.registrationOpen !== undefined) {
            // Utiliser le statut manuel si défini
            registrationStatus = competition.registrationOpen ? 'Ouvertes' : 'Fermées';
          } else {
            // Calcul automatique basé sur la date limite
            const registrationDeadlineDate = competition.registrationDeadline ? new Date(competition.registrationDeadline) : new Date(startDate.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (now < registrationDeadlineDate) {
              registrationStatus = 'Ouvertes';
            }
          }

          // Adapter les données pour correspondre au format attendu
          const adaptedCompetition: Competition = {
            id: competition.id,
            date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/\./g, '').toUpperCase(),
            title: competition.title,
            level: competition.category || 'Tous niveaux',
            duration: '3 jours', // Valeur par défaut car non présente dans Event
            description: competition.description || 'Compétition de robotique passionnante',
            location: competition.location || 'À définir',
            prize: 'Trophées et médailles', // Valeur par défaut
            prizes: 'Trophées et médailles', // Valeur par défaut
            registrationDeadline: competition.registrationDeadline ?
              new Date(competition.registrationDeadline).toLocaleDateString('fr-FR') :
              'À définir',
            maxParticipants: competition.maxParticipants || 50,
            participants: competition.registeredParticipants || 0,
            registrationPrice: competition.price ? parseFloat(competition.price) || 0 : 0,
            competitionStatus,
            registrationStatus,
            status: competition.status,
            dateStart: competition.dateStart,
            dateEnd: competition.dateEnd || competition.dateStart,
            registrationUrl: competition.registrationUrl ?? "",
            hidePricing: competition.hidePricing,
            technologies: competition.technologies || []
          };

          competitionsByYear[year].push(adaptedCompetition);
        });

        // Trier les compétitions par date dans chaque année
        Object.keys(competitionsByYear).forEach((yearKey: string) => {
          const yearNum = parseInt(yearKey);
          competitionsByYear[yearNum].sort((a: Competition, b: Competition) =>
            new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime()
          );
        });

        const sortedYears = Array.from(years).sort((a, b) => a - b); // Années anciennes en premier
        setAvailableYears(sortedYears);
        setCompetitions(competitionsByYear);

        // Sélectionner l'année la plus récente par défaut
        if (sortedYears.length > 0) {
          setSelectedYear(sortedYears[sortedYears.length - 1]);
        }

      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const navigateYear = (direction: 'up' | 'down') => {
    const currentIndex = availableYears.indexOf(selectedYear);
    if (direction === 'up' && currentIndex > 0) {
      setSelectedYear(availableYears[currentIndex - 1]);
    } else if (direction === 'down' && currentIndex < availableYears.length - 1) {
      setSelectedYear(availableYears[currentIndex + 1]);
    }
  };

  const navigation = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/formations', label: 'Formations', icon: GraduationCap },
    { path: '/competitions', label: 'Compétitions', icon: Trophy },
    { path: '/projets', label: 'Projets', icon: Code },
    { path: '/boutique', label: 'Boutique', icon: ShoppingBag }
  ];

  const currentCompetitions = competitions[selectedYear] || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-black flex items-center justify-center">
        <div className="text-white text-xl">Chargement des compétitions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-black text-white">
      {/* Header */}
      <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-lg' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
              />
              <span className="lg:text-2xl md:text-xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
                Phoelynx France
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center lg:space-x-8 md:space-x-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-1 lg:space-x-2 lg:px-4 md:px-2 py-2 rounded-lg transition-all hover:bg-white/10 ${
                      item.path === '/competitions' ? 'bg-red-500/20 text-red-300' : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="lg:text-base md:text-sm">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-white/10">
              <div className="flex flex-col space-y-2 pt-4">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all hover:bg-white/10 ${
                        item.path === '/competitions' ? 'bg-red-500/20 text-red-300' : 'text-gray-300 hover:text-white'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors mb-8"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Retour à l'accueil</span>
            </Link>

            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                Compétitions
              </span>
            </h1>

            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Découvrez l'historique complet de nos compétitions de robotique.
              Explorez nos performances année par année et préparez-vous pour les prochains défis.
            </p>
          </div>

          {/* Year Navigation Slider */}
          <div className="flex items-center justify-center mb-12">
            <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigateYear('up')}
                  disabled={availableYears.indexOf(selectedYear) === 0}
                  className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Années précédentes"
                >
                  <ChevronLeft className="h-5 w-5 text-blue-400" />
                </button>

                <div className="text-center min-w-[120px]">
                  <div className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                    {selectedYear}
                  </div>
                  <div className="text-xs text-gray-400">
                    {currentCompetitions.length} compétition{currentCompetitions.length > 1 ? 's' : ''}
                  </div>
                </div>

                <button
                  onClick={() => navigateYear('down')}
                  disabled={availableYears.indexOf(selectedYear) === availableYears.length - 1}
                  className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Années suivantes"
                >
                  <ChevronRight className="h-5 w-5 text-blue-400" />
                </button>
              </div>

              {/* Year indicators */}
              <div className="flex justify-center space-x-2 mt-4">
                {availableYears.map(year => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      year === selectedYear
                        ? 'bg-yellow-500 scale-125'
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Competitions Grid */}
          {currentCompetitions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentCompetitions.map((competition: Competition) => (
                <div
                  key={competition.id}
                  className="bg-black/30 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-yellow-500/50 transition-all duration-300 group cursor-pointer"
                  onClick={() => setSelectedCompetition(competition)}
                >
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg group-hover:scale-105 transition-transform">
                          <Trophy className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-400">{competition.date}</div>
                          <div className="text-lg font-semibold text-white">{competition.level}</div>
                        </div>
                      </div>
                    </div>
                    {/* Badges de statuts */}
                    <div className="flex flex-wrap gap-2">
                      <div className={`px-4 py-2 rounded-full text-xs font-medium text-center inline-flex items-center justify-center min-w-[80px] ${
                        competition.competitionStatus === 'Passée' ? 'bg-gray-500/20 text-gray-400' :
                        competition.competitionStatus === 'En cours' ? 'bg-green-500/20 text-green-400' :
                        competition.competitionStatus === 'À venir' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {competition.competitionStatus}
                      </div>
                      <div className={`px-4 py-2 rounded-full text-xs font-medium text-center inline-flex items-center justify-center min-w-[120px] ${
                        competition.registrationStatus === 'Ouvertes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {`Inscriptions ${competition.registrationStatus}`}
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-yellow-400 transition-colors">
                    {competition.title}
                  </h3>

                  <p className="text-gray-300 mb-4 line-clamp-2">
                    {competition.description}
                  </p>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Clock className="h-4 w-4" />
                      <span>{competition.duration}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Globe className="h-4 w-4" />
                      <span>{competition.location}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-300">
                      <Users className="h-4 w-4" />
                      <span>{competition.maxParticipants} participants max</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      {!competition.hidePricing && (
                        <span className="text-yellow-400 font-semibold">
                          {competition.registrationPrice > 0 ? `${competition.registrationPrice}€` : 'Gratuit'}
                        </span>
                      )}
                      <span className={`text-xs text-gray-400 ${!competition.hidePricing ? '' : 'ml-auto'}`}>
                        Cliquer pour plus de détails
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Trophy className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-400 mb-2">
                Aucune compétition pour {selectedYear}
              </h3>
              <p className="text-gray-500">
                Explorez les autres années ou revenez plus tard pour de nouvelles compétitions.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Competition Detail Modal */}
      {selectedCompetition && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCompetition(null)}
        >
          <div
            className="bg-black/90 backdrop-blur-lg rounded-2xl border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl">
                    <Trophy className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedCompetition.title}</h2>
                    <p className="text-gray-400">{selectedCompetition.date}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCompetition(null)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Description</h3>
                  <p className="text-gray-300">{selectedCompetition.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Informations pratiques</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3 text-gray-300">
                        <Calendar className="h-5 w-5 text-yellow-500" />
                        <div>
                          <div className="font-medium">Durée</div>
                          <div className="text-sm">{selectedCompetition.duration}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 text-gray-300">
                        <Globe className="h-5 w-5 text-yellow-500" />
                        <div>
                          <div className="font-medium">Lieu</div>
                          <div className="text-sm">{selectedCompetition.location}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 text-gray-300">
                        <Users className="h-5 w-5 text-yellow-500" />
                        <div>
                          <div className="font-medium">Participants</div>
                          <div className="text-sm">{selectedCompetition.maxParticipants} maximum</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Récompenses</h3>
                    <div className="flex items-start space-x-3 text-gray-300">
                      <Award className="h-5 w-5 text-yellow-500 mt-1" />
                      <div>
                        <div className="font-medium">Prix à gagner</div>
                        <div className="text-sm">{selectedCompetition.prizes}</div>
                      </div>
                    </div>

                    {!selectedCompetition.hidePricing && (
                      <div className="mt-6">
                        <div className="text-2xl font-bold text-yellow-400">
                          {selectedCompetition.registrationPrice > 0 ? `${selectedCompetition.registrationPrice}€` : 'Gratuit'}
                        </div>
                        <div className="text-sm text-gray-400">Frais d'inscription</div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedCompetition.registrationDeadline !== 'À définir' && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                      <div>
                        <div className="font-medium text-yellow-300">Date limite d'inscription</div>
                        <div className="text-sm text-yellow-200">{selectedCompetition.registrationDeadline}</div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedCompetition.registrationUrl && (
                  <div className="mt-6 pt-6 border-t border-white/20">
                    <a
                      href={selectedCompetition.registrationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 group"
                    >
                      <span>S'inscrire à la compétition</span>
                      <svg
                        className="h-5 w-5 group-hover:translate-x-1 transition-transform"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-lg border-t border-white/10 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
                Phoelynx France
              </span>
            </div>
            <p className="text-gray-400 mb-6">
              Innovation • Robotique • Excellence
            </p>
            <div className="text-sm text-gray-500">
              © 2024 Phoelynx France. Tous droits réservés.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CompetitionsPage;